import { type FC } from 'react';

import { formatUsd } from '../../../tools/helpers';
import { useInstanceLines } from '../hooks/useInstanceLines';
import { selectedInstanceAddons } from '../utils/instanceLineAddons';

/**
 * The add-ons a membership was sold, from its frozen instance lines — same derivation as the job
 * panel. Block variant renders a labeled list (new card); inline renders a comma-joined string
 * with an em-dash fallback (legacy InfoTable cell). Renders nothing / '—' when there are none.
 */
export const MembershipAddonsList: FC<{
  membershipId: number;
  isCommercial: boolean;
  inline?: boolean;
  className?: string;
}> = ({ membershipId, isCommercial, inline = false, className }) => {
  const linesQuery = useInstanceLines(membershipId, true);
  const addons = selectedInstanceAddons(linesQuery.data ?? [], isCommercial);

  if (inline) {
    if (addons.length === 0) return <>—</>;
    return <>{addons.map((a) => `${a.name} (${formatUsd(a.cost)})`).join(', ')}</>;
  }

  if (addons.length === 0) return null;
  return (
    <div className={className}>
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Add-ons</p>
      <ul className="mt-1 space-y-0.5">
        {addons.map((addon) => (
          <li key={addon.id} className="text-xs text-gray-600">
            {addon.name} — {formatUsd(addon.cost)}
          </li>
        ))}
      </ul>
    </div>
  );
};
